import { defineComponent, ref, watch, computed, nextTick } from "vue";
import uniqueId from "./component-library249.mjs";
import isEqual from "./component-library272.mjs";
import isNull from "./component-library260.mjs";
import isUndefined from "./component-library261.mjs";
import uniq from "./component-library273.mjs";
import without from "./component-library274.mjs";
import debounce from "./component-library233.mjs";
import Dropdown from "./component-library72.mjs";
import DropdownItem from "./component-library74.mjs";
import TextInput from "./component-library17.mjs";
import LinkButton from "./component-library3.mjs";
import { filteredOptions } from "./component-library77.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "list-dropdown",
  props: {
    placeholder: { default: void 0 },
    value: { default: null },
    options: null,
    multiple: { type: Boolean, default: false },
    includeBlank: { default: void 0 },
    labelOnSelection: { type: Boolean, default: true },
    itemUrl: { type: Function, default: void 0 },
    buttonText: { default: "Übernehmen" },
    opened: { type: Boolean },
    disabled: { type: Boolean },
    hasSearch: { type: Boolean },
    autoWidth: { type: Boolean },
    groupEntries: { type: Boolean },
    breakText: { type: Boolean },
    emptyText: { default: "Kein Eintrag vorhanden" },
    showEmptyContent: { type: Boolean }
  },
  emits: ["unselect", "select", "input", "change", "toggle"],
  setup(__props, { expose, emit }) {
    const props = __props;
    function sanitizeValue(value, multiple) {
      if (isUndefined(value) || isNull(value)) {
        return multiple ? [] : null;
      }
      return value;
    }
    const internalValue = ref(props.multiple ? [] : void 0);
    const searchInput = ref("");
    const debouncedSearchInput = ref("");
    const dropdownElement = ref(null);
    const searchInputElement = ref(null);
    const updateSearchResults = debounce(() => {
      debouncedSearchInput.value = searchInput.value;
    }, 200);
    watch(
      () => props.value,
      (value) => {
        internalValue.value = sanitizeValue(value, props.multiple);
      },
      {
        immediate: true
      }
    );
    const selectedValues = computed(() => {
      if (internalValue.value === void 0)
        return [];
      if (props.multiple)
        return internalValue.value;
      return [internalValue.value];
    });
    const isSelected = (value) => selectedValues.value.some((selectedValue) => isEqual(selectedValue, value));
    const selectedSingle = computed(() => {
      if (props.multiple || !props.options)
        return void 0;
      return props.options.find((option) => isSelected(option.value));
    });
    const selectedMultiple = computed(() => {
      if (!props.multiple || !props.options)
        return [];
      return props.options.filter((option) => isSelected(option.value));
    });
    const selection = computed(() => {
      if (props.multiple)
        return selectedMultiple.value;
      return selectedSingle.value;
    });
    const hasSelection = computed(() => {
      var _a;
      if (!props.multiple)
        return !!selection.value;
      return ((_a = selection.value) == null ? void 0 : _a.length) > 0;
    });
    const selectedLabels = computed(() => {
      var _a;
      if (!hasSelection.value)
        return "";
      if (!props.multiple)
        return (_a = selectedSingle.value) == null ? void 0 : _a.label;
      return selectedMultiple.value.map((option) => option.label).sort().join(", ");
    });
    const selectedOption = computed(() => {
      if (!hasSelection.value || props.multiple)
        return void 0;
      return selectedSingle.value;
    });
    const hasChanged = computed(() => !isEqual(props.value, internalValue.value));
    const containerClass = computed(() => {
      return {
        "dropdown--simple": !props.multiple,
        "dropdown--multi": props.multiple
      };
    });
    const filteredOptions$1 = computed(
      () => filteredOptions(debouncedSearchInput.value, props.options || [], props.groupEntries)
    );
    const isDisabled = computed(
      () => {
        var _a;
        return props.disabled || !props.showEmptyContent && (((_a = props.options) == null ? void 0 : _a.length) || 0) === 0;
      }
    );
    const reset = () => {
      internalValue.value = props.value;
    };
    const onToggle = (value) => {
      emit("toggle", value);
      if (!value) {
        reset();
        return;
      }
      nextTick(() => {
        if (searchInputElement.value) {
          searchInputElement.value.focus();
        }
      });
    };
    const select = (option) => {
      if (option === void 0) {
        internalValue.value = props.multiple ? [] : void 0;
      } else if (props.multiple) {
        internalValue.value = uniq([...internalValue.value, option == null ? void 0 : option.value]);
      } else {
        internalValue.value = option == null ? void 0 : option.value;
      }
      emit("select", option == null ? void 0 : option.value, internalValue.value);
    };
    const unselect = (option) => {
      if (props.multiple) {
        internalValue.value = without(internalValue.value, option.value);
      } else if (internalValue.value === option.value) {
        internalValue.value = void 0;
      }
      emit("unselect", option.value, internalValue.value);
    };
    const toggleSelection = (option) => {
      if (isSelected(option.value)) {
        unselect(option);
      } else {
        select(option);
      }
    };
    const unselectAll = () => {
      internalValue.value = props.multiple ? [] : void 0;
    };
    const change = () => {
      var _a;
      emit("input", internalValue.value);
      emit("change", selection.value);
      (_a = dropdownElement.value) == null ? void 0 : _a.close();
    };
    const selectAndMaybeChange = (option) => {
      if (option === void 0 || option === null) {
        unselectAll();
      } else if (props.multiple) {
        toggleSelection(option);
      } else {
        select(option);
      }
      if (!props.multiple)
        change();
    };
    const buildItemUrl = (value) => {
      if (!props.itemUrl || !value)
        return void 0;
      return props.itemUrl(value);
    };
    expose({
      internalValue,
      select,
      unselect,
      isSelected,
      selection,
      filteredOptions: filteredOptions$1,
      debouncedSearchInput,
      selectedValues,
      hasChanged,
      dropdownElement
    });
    return { __sfc: true, sanitizeValue, props, emit, internalValue, searchInput, debouncedSearchInput, dropdownElement, searchInputElement, updateSearchResults, selectedValues, isSelected, selectedSingle, selectedMultiple, selection, hasSelection, selectedLabels, selectedOption, hasChanged, containerClass, filteredOptions: filteredOptions$1, isDisabled, reset, onToggle, select, unselect, toggleSelection, unselectAll, change, selectAndMaybeChange, buildItemUrl, uniqueId, Dropdown, DropdownItem, TextInput, LinkButton };
  }
});
export {
  _sfc_main as default
};
